
import {
  computed, defineComponent, PropType, toRef, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Dropdown from 'primevue/dropdown';
import AmountStepper from '@/components/billing/dialogs/AmountStepper.vue';
import { useSmsCountries } from '@/composables/billing/useSmsCountries';
import { billingCurrencySymbol } from '@/composables/billing/formatAmount';

const AMOUNT_STEPS = [
  10, 25, 50, 75, 100, 125, 150, 200, 500, 1000, 2000, 3000, 5000, 10000,
  20000, 50000, 100000,
];

export default defineComponent({
  name: 'WalletTopUpSelection',

  components: { Dropdown, AmountStepper },

  props: {
    amount: {
      type: Number,
      required: true,
    },

    country: {
      type: String,
      required: true,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    /** Shop billing currency — drives the stepper suffix + unit-rate symbol. */
    currency: {
      type: String as PropType<'EUR' | 'USD'>,
      default: 'EUR',
    },
  },

  emits: ['update:amount', 'update:country'],

  setup(props) {
    const { t } = useI18n();
    const sms = useSmsCountries('oneShoot');
    const currencySymbol = computed(() => billingCurrencySymbol(props.currency));

    // Keep our `country` prop in sync with the composable's selection so the
    // estimation (selectedCountryData) reflects the parent-controlled value.
    watch(
      () => props.country,
      (v) => { sms.selectedCountry.value = v; },
      { immediate: true },
    );

    const amountRef = toRef(props, 'amount');
    const estimatedSms = computed(() => sms.estimatedSmsCount(amountRef.value));

    const formatUnitPrice = (n: number) => {
      const value = n.toFixed(4).replace(/0+$/, '').replace(/\.$/, '');
      return props.currency === 'USD'
        ? `${currencySymbol.value}${value}`
        : `${value} ${currencySymbol.value}`;
    };
    const formatNumber = (n: number) => new Intl.NumberFormat('fr-FR').format(n);

    return {
      t,
      AMOUNT_STEPS,
      selectedCountryData: sms.selectedCountryData,
      countryOptions: sms.countryOptions,
      countryLabelOf: sms.countryLabelOf,
      flagEmoji: sms.flagEmoji,
      estimatedSms,
      currencySymbol,
      formatUnitPrice,
      formatNumber,
    };
  },
});
